import { StyledButton } from '@app/components/button/Button';
import type { liveAgentVoices, realtimeAgentVoices, voiceAgentModels } from '@app/utils/constants';
import { Flex } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IoPlay, IoStop } from 'react-icons/io5';

type AgentVoicePreview = {
    voice: (typeof realtimeAgentVoices)[number] | (typeof liveAgentVoices)[number];
    model: (typeof voiceAgentModels)[number];
    label?: ReactNode;
};

export const AgentVoicePreview = ({ voice, model, label = 'Preview Voice' }: AgentVoicePreview) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [prevVoice, setPrevVoice] = useState(voice);

    if (voice !== prevVoice) {
        setPrevVoice(voice);
        if (isPlaying) {
            setIsPlaying(false);
        }
    }

    useEffect(() => {
        audioRef.current?.pause();
    }, [voice]);

    if (!voice) {
        return null;
    }

    const stop = () => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
        setIsPlaying(false);
    };

    const play = () => {
        audioRef.current?.play();
        setIsPlaying(true);
    };

    return (
        <figure style={{ margin: 0 }}>
            <figcaption>
                <Flex align="center" gap={5}>
                    <small onClick={() => document.getElementById('preview-voice')?.click()}>
                        {label}
                    </small>
                    <StyledButton
                        id="preview-voice"
                        htmlType="button"
                        size="small"
                        shape="round"
                        style={{ height: 20, minWidth: 20, paddingInline: 5 }}
                        onClick={() => (isPlaying ? stop() : play())}
                        aria-label={`${isPlaying ? 'Stop' : 'Play'} preview of ${voice} voice`}>
                        {isPlaying ? <IoStop /> : <IoPlay />}
                    </StyledButton>
                </Flex>
            </figcaption>
            <audio
                ref={audioRef}
                src={
                    model === 'gpt-live-1'
                        ? `/agent-voices/live/${voice}.wav`
                        : `/agent-voices/realtime/${voice}.mp3`
                }
                onEnded={() => setIsPlaying(false)}
            />
        </figure>
    );
};
